<script lang="ts">
import { defineComponent } from 'vue'

/**
 * Skeleton loader for individual instant win game page
 * Matches structure of InstantWinAttractWrapper.vue
 */
export default defineComponent({
  name: 'InstantWinPageSkeleton',
})
</script>

<template>
  <div class="animate-pulse">
    <!-- Main Section (matches InstantWinAttractWrapper layout) -->
    <div class="grid">
      <div class="row-start-1 col-start-1 min-w-0">
        <div
          class="flex w-full max-w-screen-xl flex-wrap gap-8 mx-auto py-6 px-3 @md/main:px-8"
        >
          <!-- Left side: Hero + Stats -->
          <div
            class="z-0 flex w-full flex-col flex-2 justify-center flex-1 gap-8 items-center"
          >
            <!-- Hero content -->
            <div class="w-full">
              <!-- Prize carousel -->
              <div class="flex justify-center gap-2 mb-2">
                <div
                  v-for="i in 5"
                  :key="i"
                  class="h-20 w-16 rounded bg-slate-200"
                />
              </div>
              <!-- Main product image -->
              <div
                class="mx-auto h-[240px] @sm/main:h-[320px] w-full max-w-xs rounded-lg bg-slate-200"
              />
            </div>

            <!-- Stats section -->
            <div
              class="grid grid-cols-1 @xs/main:grid-cols-2 gap-3 text-center w-full p-3 rounded-lg bg-primary-lighter"
            >
              <div
                class="flex flex-col justify-center items-center p-2 rounded-lg h-[60px] bg-pure-white"
              >
                <div class="h-6 w-20 rounded bg-slate-300 mb-1" />
                <div class="h-4 w-24 rounded bg-slate-200" />
              </div>
              <div
                class="flex flex-col justify-center items-center p-2 rounded-lg h-[60px] bg-pure-white"
              >
                <div class="h-6 w-16 rounded bg-slate-300 mb-1" />
                <div class="h-4 w-32 rounded bg-slate-200" />
              </div>
              <!-- Main prize info -->
              <div
                class="col-start-1 -col-end-1 flex justify-center gap-1 py-1"
              >
                <div class="h-5 w-48 rounded bg-slate-300" />
              </div>
            </div>
          </div>

          <!-- Right side: Checkout form -->
          <div class="hidden @lg/main:block w-[440px] self-center">
            <div
              class="rounded-xl bg-white p-6 shadow-sm border border-slate-200"
            >
              <!-- Title -->
              <div class="h-7 w-3/4 rounded bg-slate-300 mb-4" />

              <!-- Tabs (Online / Free Postal Entry) -->
              <div class="flex gap-4 border-b border-slate-200 pb-3 mb-4">
                <div class="h-5 w-16 rounded bg-slate-300" />
                <div class="h-5 w-28 rounded bg-slate-200" />
              </div>

              <!-- Entry form area -->
              <div class="space-y-4 rounded-lg border border-slate-200 p-4">
                <div class="flex justify-between">
                  <div class="h-5 w-28 rounded bg-slate-200" />
                  <div class="h-5 w-24 rounded bg-slate-200" />
                </div>
                <!-- Entry buttons -->
                <div class="flex gap-2">
                  <div
                    v-for="i in 4"
                    :key="i"
                    class="h-10 flex-1 rounded bg-slate-200"
                  />
                </div>
                <!-- Entry input -->
                <div
                  class="h-10 w-full rounded border border-slate-200 bg-slate-50"
                />
                <!-- Slider -->
                <div class="flex items-center gap-3">
                  <div class="h-10 w-10 rounded bg-slate-300" />
                  <div class="h-2 flex-1 rounded-full bg-slate-200" />
                  <div class="h-10 w-10 rounded bg-slate-300" />
                </div>
                <!-- Total -->
                <div class="flex justify-center">
                  <div class="h-5 w-24 rounded bg-slate-200" />
                </div>
              </div>

              <!-- CTA Buttons -->
              <div class="flex gap-3 mt-4">
                <div class="h-12 w-32 rounded-lg bg-slate-200" />
                <div class="h-12 flex-1 rounded-full bg-slate-300" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile bottom bar -->
    <div
      class="grid grid-cols-2 @xs/main:grid-cols-5 gap-4 justify-center fixed bottom-0 z-20 p-5 bg-pure-white w-full rounded-t-xl shadow-[0_0_3px_#0004] @lg/main:hidden"
    >
      <div
        class="h-12 rounded-lg bg-slate-200 col-span-1 @xs/main:col-span-2"
      />
      <div
        class="h-12 rounded-full bg-slate-300 col-span-1 @xs/main:col-span-3"
      />
    </div>

    <!-- Content Section -->
    <div class="bg-pure-white">
      <AppSection>
        <!-- Filter Tabs (Prizes / Details / FAQ) -->
        <div class="flex gap-2 p-2 -mt-2 -mb-1">
          <div
            class="h-10 w-24 rounded-full border border-slate-200 bg-slate-100"
          />
          <div class="h-10 w-24 rounded-full border border-slate-200" />
          <div class="h-10 w-16 rounded-full border border-slate-200" />
        </div>

        <!-- Prize Grid -->
        <div
          class="grid grid-cols-2 gap-3 @sm/main:grid-cols-3 @md/main:grid-cols-4 @lg/main:grid-cols-5 @xl/main:grid-cols-6"
        >
          <div
            v-for="i in 6"
            :key="i"
            class="rounded-lg border border-slate-200 bg-white overflow-hidden"
          >
            <!-- Prize image -->
            <div class="aspect-square w-full bg-slate-200" />
            <!-- Prize info -->
            <div class="p-3">
              <div class="h-5 w-full rounded bg-slate-200 mb-2" />
              <div class="h-4 w-2/3 rounded bg-slate-200 mb-3" />
              <!-- View more button -->
              <div class="h-8 w-full rounded bg-slate-100" />
            </div>
          </div>
        </div>
      </AppSection>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>
